import { BaseSyntheticEvent } from 'react'
import {
	FieldErrors,
	FieldValues,
	SubmitErrorHandler,
	SubmitHandler,
} from 'react-hook-form'
import { getFirstInvalidInput } from 'utils/formUtils'

import {
	OPEN_MENU_PRODUCT_ACCORDION_EVENT,
	OPEN_MENU_SECTION_ACCORDION_EVENT,
} from './MenuForm'
import { TMenuFormFields } from './menuFormModel'

type ValidationError = {
	sectionIndex: number
	productIndex: number | null
}

export const findFirstValidationError = (
	sections: FieldErrors<TMenuFormFields>['sections'],
): ValidationError | null => {
	if (!Array.isArray(sections)) return null

	for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
		const sectionError = sections[sectionIndex]
		if (!sectionError || typeof sectionError !== 'object') continue

		const { products, ...otherErrors } = sectionError as Record<string, unknown>
		if (Object.values(otherErrors).some(Boolean)) {
			return { sectionIndex, productIndex: null }
		}

		if (Array.isArray(products)) {
			const productIndex = products.findIndex(Boolean)
			if (productIndex !== -1) {
				return { sectionIndex, productIndex }
			}
		}
	}

	return null
}

export const validateMenuForm = <T extends FieldValues>(
	handleSubmit: (
		onValid: SubmitHandler<T>,
		onInvalid: SubmitErrorHandler<T>,
	) => (e?: BaseSyntheticEvent) => Promise<void>,
	scrollDelay: number = 0,
	bus: { emit: (event: string, payload: unknown) => void },
) =>
	new Promise<{ isValid: boolean }>((resolve) => {
		handleSubmit(
			() => resolve({ isValid: true }),
			(errors) => {
				resolve({ isValid: false })

				const firstError = findFirstValidationError(
					errors.sections as FieldErrors<TMenuFormFields>['sections'],
				)
				const firstInvalid = getFirstInvalidInput(errors)

				if (firstError) {
					bus.emit(OPEN_MENU_SECTION_ACCORDION_EVENT, {
						sectionIndex: firstError.sectionIndex,
					})

					if (firstError.productIndex !== null) {
						bus.emit(OPEN_MENU_PRODUCT_ACCORDION_EVENT, {
							sectionIndex: firstError.sectionIndex,
							productIndex: firstError.productIndex,
						})
					}
				}

				setTimeout(() => {
					if (firstInvalid?.ref) {
						const element = firstInvalid.ref
						const elementName = element.getAttribute('name')
						let targetElement: HTMLElement | null = null

						if (elementName) {
							targetElement = document.querySelector(
								`[name="${elementName}"]`,
							) as HTMLElement
						}

						if (targetElement) {
							if (targetElement.focus) {
								targetElement.focus({ preventScroll: true })
							}

							requestAnimationFrame(() => {
								targetElement!.scrollIntoView({
									block: 'center',
									behavior: 'smooth',
								})
							})
						}
					}
				}, scrollDelay)
			},
		)()
	})
